import { RelationshipUi, RelListFilterContainerUi, useReadRelationshipList } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { useEffect, useMemo, useState } from 'react'
import { useGetRelationshipTypeWrapper } from '@isdd/metais-common/hooks/useRelationshipType.hook'
import {
    BASE_PAGE_NUMBER,
    BASE_PAGE_SIZE,
    calculateConstraintFromAttributeProfiles,
    calculateConstraintFromAttributes,
} from '@isdd/metais-common/index'
import { useHowToDisplayConstraints } from '@isdd/metais-common/hooks/useHowToDisplay'
import { SortType } from '@isdd/idsk-ui-kit/types'
import { IFilterParams, useFilterParams } from '@isdd/metais-common/hooks/useFilter'
import { useUserPreferences } from '@isdd/metais-common/contexts/userPreferences/userPreferencesContext'
import { mapFilterParamsToApi } from '@isdd/metais-common/componentHelpers'
import { useParams } from 'react-router-dom'
import { getMetaAttributesForCiFilter } from '@isdd/metais-common/componentHelpers/ci/filter'

import { IRelationTypeListView } from '../views/relation-type-list/RelationTypeListView'

import { RelationshipsFilterData } from './relation-list/RelationListContainer'

interface Props {
    View: React.FC<IRelationTypeListView>
}

export interface RelationFilterData extends IFilterParams {
    startCIs?: string[]
    endCIs?: string[]
}

export const defaultFilterValues: RelationFilterData = {
    startCIs: [],
    endCIs: [],
}

export const RelationTypeListContainer: React.FC<Props> = ({ View }) => {
    const { currentPreferences } = useUserPreferences()
    const { relationType } = useParams()

    const [exportPayload, setExportPayload] = useState<RelListFilterContainerUi>()

    const { mutateAsync, isPending: isRelationsLoading } = useReadRelationshipList()

    const { filter: rawFilter, handleFilterChange } = useFilterParams<RelationshipsFilterData>({
        ...defaultFilterValues,
        sort: [
            {
                orderBy: 'startName',
                sortDirection: SortType.DESC,
            },
        ],
        metaAttributeFilters: { state: currentPreferences.showInvalidatedItems ? ['DRAFT', 'INVALIDATED'] : ['DRAFT'] },
        pageNumber: BASE_PAGE_NUMBER,
        pageSize: BASE_PAGE_SIZE,
    })

    const {
        data: relationTypeData,
        isLoading: isRelationTypeDataLoading,
        isError: isRelationTypeDataError,
    } = useGetRelationshipTypeWrapper(relationType ?? '')

    const constraintsAttributes = calculateConstraintFromAttributes(relationTypeData?.attributes)

    const constraintsAttributesProfiles = calculateConstraintFromAttributeProfiles(relationTypeData?.attributeProfiles ?? [], true)

    const constraints = [...constraintsAttributes, ...constraintsAttributesProfiles]

    const { isLoading: isConstraintLoading, isError: isConstraintError, resultList } = useHowToDisplayConstraints(constraints)

    const constraintsData = resultList.map((item) => item.data)

    const [relationships, setRelationships] = useState<RelationshipUi[]>([])
    const [totaltems, setTotalItems] = useState<number>(0)
    const [totalPages, setTotalPages] = useState<number>(0)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filter = useMemo(() => rawFilter, [JSON.stringify(rawFilter)])

    useEffect(() => {
        const payload: RelListFilterContainerUi = {
            relFilter: {
                type: [relationType ?? ''],
                attributes: mapFilterParamsToApi({ ...filter, evidence_status: undefined }, {}).filter(
                    (attr) => attr.name !== 'startCIs' && attr.name !== 'endCIs',
                ),
                endCiUuid: filter.endCIs,
                startCiUuid: filter.startCIs,
                fullTextSearch: filter.fullTextSearch,
                metaAttributes: getMetaAttributesForCiFilter({
                    myPO: currentPreferences.myPO,
                    showInvalidatedItems: currentPreferences.showInvalidatedItems,
                    evidenceStatus: filter.evidence_status,
                    metaAttributeFilters: filter.metaAttributeFilters,
                }),
            },

            perpage: filter.pageSize,
            page: filter.pageNumber,
            sortBy: filter.sort?.[0]?.orderBy,
            sortType: filter.sort?.[0]?.sortDirection,
        }
        setExportPayload(payload)
        mutateAsync(
            { data: payload },
            {
                onSuccess(res) {
                    setRelationships(res.relationshipList ?? [])
                    setTotalItems(res.pagination?.totaltems ?? 0)
                    setTotalPages(res.pagination?.totalPages ?? 0)
                },
            },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter])

    const isLoading = isConstraintLoading || isRelationTypeDataLoading || isRelationsLoading

    return (
        <View
            relationTypeData={relationTypeData ?? {}}
            relations={relationships}
            attributes={relationTypeData?.attributes}
            attributeProfiles={relationTypeData?.attributeProfiles}
            constraintsData={constraintsData}
            defaultFilterValues={defaultFilterValues}
            handleFilterChange={handleFilterChange}
            filter={rawFilter}
            totalItems={totaltems}
            totalPages={totalPages}
            isLoading={isLoading}
            isError={isConstraintError || isRelationTypeDataError}
            exportPayload={exportPayload ?? {}}
        />
    )
}
